import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCogs, faPlay, faStop, faSync, faExclamationTriangle, 
  faCheckCircle, faCloudDownloadAlt, faTrash, faClock,
  faEnvelope, faChartLine, faBug, faServer, faHistory,
  faCalendarAlt, faRobot, faBell, faFileAlt, faShieldAlt,
  faTimesCircle, faSpinner, faDatabase, faUsers, faClipboardList,
  faInfoCircle, faCopy, faDownload, faRefresh, faPause, faTags
} from '@fortawesome/free-solid-svg-icons';
import { useToast } from '../components/ui/ToastProvider';
import LoadingSpinner from '../components/ui/LoadingSpinner';

import { WorkflowStatus, WorkflowLog, WorkflowConfig } from '../types/workflow';
import { 
  getWorkflowStatus, 
  getWorkflowLogs,
  triggerReminderProcessing,
  triggerOverdueCheck,
  generateManualReport,
  triggerManualCleanup,
  testEmailConnection,
  updateWorkflowConfig,
  PerfionSyncTask,
  getPerfionSyncStatus,
  triggerArticleSync,
  triggerSupplierSync
} from '../services/workflowApi';
import { syncFromPerfionAPI } from '../services/articleApi';
import { syncSuppliersFromPerfionAPI } from '../services/supplierApi';

import SystemHealthWidget from '../components/workflow/SystemHealthWidget';
import WorkflowStatusCard from '../components/workflow/WorkflowStatusCard';
import WorkflowConfigModal from '../components/workflow/WorkflowConfigModal';
import WorkflowLogsViewer from '../components/workflow/WorkflowLogsViewer';
import ECRImportModal from '../components/workflow/ECRImportModal';
import BrandImportModal from '../components/forms/BrandImportModal';
import { LabelImportModal } from '../components/forms/LabelImportModal';
import LineImportModal from '../components/workflow/LineImportModal';
import DropdownImportModal from '../components/workflow/DropdownImportModal';
import { ECRCode } from '../types/ecrCode';
import { Brand } from '../types/brand';
import { Label } from '../types/label';
import { Line } from '../services/lineApi';
import { ecrCodeService } from '../services/ecrCodeService';
import { brandService } from '../services/brandService';
import { labelService, LabelImportResult } from '../services/labelService';
import { getLines } from '../services/lineApi';

// API Base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface SystemHealth {
  database: string;
  submissions: number;
  suppliers: number;
  templates: number;
  overdueCount: number;
  remindersDue: number;
  issues: string[];
  lastCheck: string;
}

interface WorkflowData {
  isRunning: boolean;
  lastRun?: string;
  nextRun?: string;
  config: WorkflowConfig;
  totalProcessed: number;
  successCount: number;
  errorCount: number;
  systemHealth: SystemHealth;
}

interface ActionResult {
  type: string;
  message: string;
  timestamp: Date;
  success: boolean;
}

const WorkflowManagement: React.FC = () => {
  // **STATE MANAGEMENT**
  const [workflowStatus, setWorkflowStatus] = useState<WorkflowData | null>(null);
  const [logs, setLogs] = useState<WorkflowLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [syncingPerfion, setSyncingPerfion] = useState(false);
  const [syncingSuppliers, setSyncingSuppliers] = useState(false);
  const [perfionSyncTasks, setPerfionSyncTasks] = useState<PerfionSyncTask[]>([]);
  
  const { showSuccess, showError } = useToast();
  
  // **MODAL STATES**
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [showECRImportModal, setShowECRImportModal] = useState(false);
  const [showBrandImportModal, setShowBrandImportModal] = useState(false);
  const [showLabelImportModal, setShowLabelImportModal] = useState(false);
  const [showLineImportModal, setShowLineImportModal] = useState(false);
  const [showDropdownImportModal, setShowDropdownImportModal] = useState<{
    isOpen: boolean;
    category: string;
    categoryLabel: string;
  }>({ isOpen: false, category: '', categoryLabel: '' });
  
  // **ACTION STATES**
  const [processingAction, setProcessingAction] = useState<string | null>(null);
  const [lastAction, setLastAction] = useState<ActionResult | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  
  // **ECR CODES, BRANDS & LABELS STATE**
  const [ecrCodes, setEcrCodes] = useState<ECRCode[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [labels, setLabels] = useState<Label[]>([]);
  const [lines, setLines] = useState<Line[]>([]);

  // **DEFAULT FALLBACK DATA**
  const createDefaultWorkflowData = (): WorkflowData => ({
    isRunning: false,
    lastRun: undefined,
    nextRun: undefined,
    config: {
      enabled: false,
      schedules: {
        reminderProcessing: '0 9 * * *',
        weeklyReports: '0 8 * * 1',
        monthlyCleanup: '0 2 1 * *',
        automaticCreation: '0 7 1 1 *',
        overdueChecks: '0 */6 * * *'
      },
      reminders: {
        initial: 14,
        reminder: 7,
        final_warning: 2,
        escalation: 3,
        escalation_repeat: 7
      },
      automation: {
        autoCreateSubmissions: false,
        autoExpireOld: false,
        autoStatusUpdates: false,
        autoReports: false
      },
      escalationContacts: [],
      reportRecipients: []
    },
    totalProcessed: 0,
    successCount: 0,
    errorCount: 0,
    systemHealth: {
      database: 'UNKNOWN',
      submissions: 0,
      suppliers: 0,
      templates: 0,
      overdueCount: 0,
      remindersDue: 0,
      issues: ['Workflow-Status konnte nicht geladen werden'],
      lastCheck: new Date().toISOString()
    }
  });

  // **DATA LOADING FUNCTION**
  const loadWorkflowData = useCallback(async (showRefreshIndicator = false) => {
    try {
      if (showRefreshIndicator) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      
      console.log('[WORKFLOW] Loading workflow data...');
      
      // **PARALLEL DATA LOADING**
      const [statusResponse, logsResponse, perfionSyncResponse] = await Promise.allSettled([
        getWorkflowStatus(),
        getWorkflowLogs({ limit: 100 }),
        getPerfionSyncStatus()
      ]);

      // **HANDLE WORKFLOW STATUS**
      if (statusResponse.status === 'fulfilled') {
        const statusData = statusResponse.value?.data;
        console.log('[WORKFLOW] Status loaded:', statusData);
        
        if (statusData) {
          setWorkflowStatus(statusData);
        } else {
          console.warn('[WORKFLOW] No status data received, using defaults');
          setWorkflowStatus(createDefaultWorkflowData());
        }
      } else {
        console.error('[WORKFLOW] Failed to load workflow status:', statusResponse.reason);
        setWorkflowStatus(createDefaultWorkflowData());
      }

      // **HANDLE LOGS**
      if (logsResponse.status === 'fulfilled') {
        const logsData = logsResponse.value?.data || [];
        console.log('[WORKFLOW] Logs loaded:', logsData.length);
        setLogs(logsData);
      } else {
        console.error('[WORKFLOW] Failed to load workflow logs:', logsResponse.reason);
        setLogs([]);
      }

      // **HANDLE PERFION SYNC STATUS**
      if (perfionSyncResponse.status === 'fulfilled') {
        const perfionData = perfionSyncResponse.value?.data?.tasks || [];
        console.log('[WORKFLOW] Perfion sync tasks loaded:', perfionData.length);
        setPerfionSyncTasks(perfionData);
      } else {
        console.error('[WORKFLOW] Failed to load perfion sync status:', perfionSyncResponse.reason);
        setPerfionSyncTasks([]);
      }

    } catch (err: any) {
      console.error('[WORKFLOW] Error loading workflow data:', err);
      setError('Fehler beim Laden der Workflow-Daten: ' + (err?.message || 'Unbekannter Fehler'));
      setWorkflowStatus(createDefaultWorkflowData());
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // **INITIAL DATA LOAD**
  useEffect(() => {
    loadWorkflowData();
    loadBrands(); // Load brands on component mount
    loadLabels(); // Load labels on component mount
    loadLines(); // Load lines on component mount
  }, [loadWorkflowData]);

  // **AUTO-REFRESH MECHANISM**
  useEffect(() => {
    if (!autoRefresh) return;

    // Check if any sync is currently running
    const isSyncRunning = perfionSyncTasks.some(t => t.lastExecutionStatus === 'running') || syncingPerfion || syncingSuppliers;
    
    // Use faster refresh interval when sync is running
    const refreshInterval = isSyncRunning ? 5000 : 30000; // 5 seconds vs 30 seconds

    const interval = setInterval(() => {
      console.log('[WORKFLOW] Auto-refresh triggered', isSyncRunning ? '(fast mode)' : '(normal mode)');
      loadWorkflowData(true);
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [loadWorkflowData, autoRefresh, perfionSyncTasks, syncingPerfion, syncingSuppliers]);

  // **GENERIC MANUAL TRIGGER HANDLER**
  const handleManualTrigger = async (
    actionType: string, 
    actionFn: () => Promise<any>, 
    actionLabel: string,
    confirmMessage?: string
  ) => {
    if (confirmMessage && !window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      setProcessingAction(actionType);
      console.log(`[WORKFLOW] Triggering ${actionType}...`);
      
      const response = await actionFn();
      
      const result: ActionResult = {
        type: actionType,
        message: response?.data?.message || response?.message || `${actionLabel} erfolgreich ausgeführt`,
        timestamp: new Date(),
        success: true
      };
      
      setLastAction(result);
      console.log(`[WORKFLOW] ${actionType} completed successfully`);
      
      // **REFRESH DATA AFTER ACTION**
      setTimeout(() => loadWorkflowData(true), 2000);
      
    } catch (error: any) {
      console.error(`[WORKFLOW] Error executing ${actionType}:`, error);
      
      const result: ActionResult = {
        type: actionType,
        message: `Fehler bei ${actionLabel}: ${error?.response?.data?.message || error?.message || 'Unbekannter Fehler'}`,
        timestamp: new Date(),
        success: false
      };
      
      setLastAction(result);
    } finally {
      setProcessingAction(null);
    }
  };

  // **SPECIFIC ACTION HANDLERS**
  const handleReminderProcessing = () => {
    handleManualTrigger(
      'reminder-processing',
      () => triggerReminderProcessing(false),
      'Erinnerungsverarbeitung',
      'Soll die manuelle Erinnerungsverarbeitung gestartet werden?'
    );
  };

  const handleOverdueCheck = () => {
    handleManualTrigger(
      'overdue-check',
      () => triggerOverdueCheck(),
      'Überfälligkeits-Check',
      'Soll die Überprüfung auf überfällige Submissions gestartet werden?'
    );
  };

  const handleWeeklyReport = () => {
    handleManualTrigger(
      'weekly-report',
      () => generateManualReport('weekly'),
      'Wochenbericht',
      'Soll der Wochenbericht erstellt werden?'
    );
  };

  const handleCleanup = () => {
    handleManualTrigger(
      'cleanup',
      () => triggerManualCleanup(false),
      'Datenbereinigung',
      'Soll die Datenbereinigung gestartet werden? Dies kann nicht rückgängig gemacht werden.'
    );
  };

  const handleTestEmail = () => {
    handleManualTrigger(
      'test-email',
      () => testEmailConnection(),
      'Email-Test'
    );
  };

  // **ECR IMPORT HANDLER**
  const handleECRImport = async (codes: ECRCode[]) => {
    try {
      setProcessingAction('ecr-import');
      
      // Import codes to API using service
      const result = await ecrCodeService.importCodes(codes);
      
      setEcrCodes(codes);
      setLastAction({
        type: 'ecr-import',
        message: result.message || `${codes.length} ECR-Codes erfolgreich importiert`,
        timestamp: new Date(),
        success: true
      });
    } catch (error) {
      console.error('Error importing ECR codes:', error);
      setLastAction({
        type: 'ecr-import',
        message: `Fehler beim Importieren: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`,
        timestamp: new Date(),
        success: false
      });
    } finally {
      setProcessingAction(null);
    }
  };

  // **BRAND IMPORT COMPLETED**
  const handleBrandImportComplete = (importedCount: number) => {
    setLastAction({
      type: 'brand-import',
      message: `${importedCount} Brands erfolgreich importiert`,
      timestamp: new Date(),
      success: true
    });
    // Brands neu laden
    loadBrands();
  };

  // **LABEL IMPORT COMPLETED**
  const handleLabelImportComplete = (result: LabelImportResult) => {
    setLastAction({
      type: 'label-import',
      message: `${result.imported} Labels importiert, ${result.updated} aktualisiert`,
      timestamp: new Date(),
      success: true
    });
    // Labels neu laden
    loadLabels();
  };

  // **LINE IMPORT COMPLETED**
  const handleLineImportComplete = () => {
    setLastAction({
      type: 'line-import',
      message: 'Linien erfolgreich importiert',
      timestamp: new Date(),
      success: true
    });
    // Lines neu laden
    loadLines();
  };

  // **DROPDOWN IMPORT COMPLETED**
  const handleDropdownImportComplete = (options: any[]) => {
    setLastAction({
      type: 'dropdown-import',
      message: `${options.length} Dropdown-Optionen erfolgreich importiert`,
      timestamp: new Date(),
      success: true
    });
    setShowDropdownImportModal({ isOpen: false, category: '', categoryLabel: '' });
    showSuccess(`${options.length} Optionen erfolgreich importiert`);
  };

  // **OPEN DROPDOWN IMPORT MODAL**
  const openDropdownImportModal = (category: string, categoryLabel: string) => {
    setShowDropdownImportModal({
      isOpen: true,
      category,
      categoryLabel
    });
  };

  const loadLabels = async () => {
    try {
      const labelsData = await labelService.getAllLabels();
      setLabels(labelsData);
    } catch (error) {
      console.error('Error loading labels:', error);
    }
  };

  const loadLines = async () => {
    try {
      const linesData = await getLines();
      setLines(linesData);
    } catch (error) {
      console.error('Error loading lines:', error);
    }
  };

  // **BRAND IMPORT HANDLERS**
  const loadBrands = async () => {
    try {
      const brandsData = await brandService.getAllBrands();
      setBrands(brandsData);
    } catch (error) {
      console.error('Error loading brands:', error);
    }
  };

  // **CONFIG UPDATE HANDLER**
  const handleConfigUpdate = async (newConfig: Partial<WorkflowConfig>) => {
    try {
      setProcessingAction('config-update');
      console.log('[WORKFLOW] Updating config:', newConfig);
      
      await updateWorkflowConfig(newConfig);
      
      setLastAction({
        type: 'config-update',
        message: 'Konfiguration erfolgreich aktualisiert',
        timestamp: new Date(),
        success: true
      });
      
      setShowConfigModal(false);
      await loadWorkflowData(true);
      
    } catch (error: any) {
      console.error('[WORKFLOW] Error updating config:', error);
      
      setLastAction({
        type: 'config-update',
        message: `Fehler beim Aktualisieren der Konfiguration: ${error?.message || 'Unbekannter Fehler'}`,
        timestamp: new Date(),
        success: false
      });
    } finally {
      setProcessingAction(null);
    }
  };

  // **UTILITY FUNCTIONS**
  const dismissAlert = () => {
    setLastAction(null);
  };

  const toggleAutoRefresh = () => {
    setAutoRefresh(!autoRefresh);
  };

  const handlePerfionSync = async () => {
    setSyncingPerfion(true);
    try {
      const response = await triggerArticleSync();
      showSuccess(response?.data?.message || 'Perfion-Artikel-Synchronisation erfolgreich gestartet');
      
      // Reload workflow data to reflect changes
      await loadWorkflowData(true);
    } catch (err: any) {
      console.error('Perfion Sync error:', err);
      showError(`Fehler beim Perfion Sync: ${err?.message || 'Unbekannter Fehler'}`);
    } finally {
      setSyncingPerfion(false);
    }
  };

  const handleSupplierPerfionSync = async () => {
    setSyncingSuppliers(true);
    try {
      const response = await triggerSupplierSync();
      showSuccess(response?.data?.message || 'Lieferanten Perfion-Synchronisation erfolgreich gestartet');
      
      // Reload workflow data to reflect changes
      await loadWorkflowData(true);
    } catch (err: any) {
      console.error('Supplier Perfion Sync error:', err);
      showError(`Fehler beim Lieferanten Perfion Sync: ${err?.message || 'Unbekannter Fehler'}`);
    } finally {
      setSyncingSuppliers(false);
    }
  };

  const formatLastRun = (timestamp?: string) => {
    if (!timestamp) return 'Nie ausgeführt';
    try {
      return new Date(timestamp).toLocaleString('de-DE');
    } catch {
      return 'Unbekannt';
    }
  };

  // **LOADING STATE**
  if (loading) {
    return <LoadingSpinner size="medium" text="Workflow-Management wird geladen..." />;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="px-4 sm:px-6 lg:px-8">
        
        {/* **HEADER SECTION** */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-lg shadow-sm p-6 mb-6"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center mb-4 lg:mb-0">
              <div className="bg-purple-500 p-3 rounded-lg mr-4">
                <FontAwesomeIcon icon={faCogs} className="text-2xl text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Workflow Management</h1>
                <p className="text-gray-600 mt-1">
                  Automatisierte Workflows überwachen und verwalten
                </p>
                {workflowStatus && (
                  <p className="text-sm text-gray-500 mt-1">
                    Letzte Ausführung: {formatLastRun(workflowStatus.lastRun)}
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowLogsModal(true)}
                className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <FontAwesomeIcon icon={faHistory} className="mr-2" />
                Logs ({logs.length})
              </button>
              
              <button
                onClick={() => setShowConfigModal(true)}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <FontAwesomeIcon icon={faCogs} className="mr-2" />
                Konfiguration
              </button>
              
              <button
                onClick={toggleAutoRefresh}
                className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                  autoRefresh 
                    ? 'bg-green-600 text-white hover:bg-green-700' 
                    : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                }`}
              >
                <FontAwesomeIcon icon={autoRefresh ? faPlay : faPause} className="mr-2" />
                Auto-Refresh
              </button>
              
              <button
                onClick={() => loadWorkflowData(true)}
                disabled={refreshing}
                className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FontAwesomeIcon 
                  icon={refreshing ? faSpinner : faRefresh} 
                  className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} 
                />
                {refreshing ? 'Aktualisiert...' : 'Aktualisieren'}
              </button>
            </div>
          </div>
        </motion.div>

        {/* **ERROR ALERT** */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6"
          >
            <div className="flex items-start">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 mr-3 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-red-800">Fehler beim Laden</h3>
                <p className="text-red-700 mt-1">{error}</p>
                <button
                  onClick={() => setError(null)}
                  className="mt-2 text-red-600 hover:text-red-800 underline"
                >
                  Schließen
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* **SUCCESS/ERROR MESSAGE** */}
        {lastAction && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`border rounded-lg p-4 mb-6 ${
              lastAction.success 
                ? 'bg-green-50 border-green-200' 
                : 'bg-red-50 border-red-200'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start">
                <FontAwesomeIcon 
                  icon={lastAction.success ? faCheckCircle : faExclamationTriangle} 
                  className={`mr-3 mt-0.5 ${lastAction.success ? 'text-green-500' : 'text-red-500'}`}
                />
                <div>
                  <h3 className={`font-semibold ${lastAction.success ? 'text-green-800' : 'text-red-800'}`}>
                    {lastAction.success ? 'Erfolgreich' : 'Fehler'}
                  </h3>
                  <p className={`mt-1 ${lastAction.success ? 'text-green-700' : 'text-red-700'}`}>
                    {lastAction.message}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    {lastAction.timestamp.toLocaleString('de-DE')}
                  </p>
                </div>
              </div>
              <button
                onClick={dismissAlert}
                className="text-gray-400 hover:text-gray-600"
              >
                <FontAwesomeIcon icon={faTimesCircle} />
              </button>
            </div>
          </motion.div>
        )}

        {/* **MAIN CONTENT** */}
        {workflowStatus && (
          <div className="space-y-6">
            
            {/* **SYSTEM HEALTH WIDGET** */}
            <SystemHealthWidget 
              health={workflowStatus.systemHealth} 
              isRunning={workflowStatus.isRunning} 
            />

            {/* **WORKFLOW STATUS OVERVIEW** */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white rounded-lg shadow-sm p-6"
            >
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Workflow-Übersicht</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <FontAwesomeIcon icon={faClipboardList} className="text-3xl text-blue-500 mb-2" />
                  <p className="text-2xl font-bold text-blue-900">{workflowStatus.totalProcessed}</p>
                  <p className="text-sm text-blue-700">Gesamt verarbeitet</p>
                </div>
                
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-3xl text-green-500 mb-2" />
                  <p className="text-2xl font-bold text-green-900">{workflowStatus.successCount}</p>
                  <p className="text-sm text-green-700">Erfolgreich</p>
                </div>
                
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="text-3xl text-red-500 mb-2" />
                  <p className="text-2xl font-bold text-red-900">{workflowStatus.errorCount}</p>
                  <p className="text-sm text-red-700">Fehler</p>
                </div>
              </div>
            </motion.div>

            {/* **WORKFLOW ACTIONS GRID** */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              <WorkflowStatusCard
                title="Erinnerungsverarbeitung"
                description="Automatische Versendung von Erinnerungen an Lieferanten"
                icon={faBell}
                color="blue"
                isActive={workflowStatus.config?.enabled || false}
                lastRun={workflowStatus.lastRun}
                schedule={workflowStatus.config?.schedules?.reminderProcessing || '0 9 * * *'}
                onManualTrigger={handleReminderProcessing}
                isProcessing={processingAction === 'reminder-processing'}
              />

              <WorkflowStatusCard
                title="Überfälligkeits-Check"
                description="Überprüfung und Update von überfälligen Submissions"
                icon={faClock}
                color="yellow"
                isActive={workflowStatus.config?.automation?.autoStatusUpdates || false}
                schedule={workflowStatus.config?.schedules?.overdueChecks || '0 */6 * * *'}
                onManualTrigger={handleOverdueCheck}
                isProcessing={processingAction === 'overdue-check'}
              />

              <WorkflowStatusCard
                title="Wöchentliche Berichte"
                description="Automatische Generierung und Versendung von Reports"
                icon={faChartLine}
                color="green"
                isActive={workflowStatus.config?.automation?.autoReports || false}
                schedule={workflowStatus.config?.schedules?.weeklyReports || '0 8 * * 1'}
                onManualTrigger={handleWeeklyReport}
                isProcessing={processingAction === 'weekly-report'}
                customAction="Bericht erstellen"
              />

              <WorkflowStatusCard
                title="Datenbereinigung"
                description="Monatliche Bereinigung alter und abgelaufener Daten"
                icon={faTrash}
                color="red"
                isActive={workflowStatus.config?.automation?.autoExpireOld || false}
                schedule={workflowStatus.config?.schedules?.monthlyCleanup || '0 2 1 * *'}
                onManualTrigger={handleCleanup}
                isProcessing={processingAction === 'cleanup'}
                customAction="Bereinigung starten"
              />

              <WorkflowStatusCard
                title="Email-Service"
                description="Test der Email-Konfiguration und Konnektivität"
                icon={faEnvelope}
                color="purple"
                isActive={true}
                onManualTrigger={handleTestEmail}
                isProcessing={processingAction === 'test-email'}
                customAction="Email testen"
              />

              <WorkflowStatusCard
                title="Jahres-Submissions"
                description="Automatische Erstellung von Submissions für neues Jahr"
                icon={faCalendarAlt}
                color="gray"
                isActive={workflowStatus.config?.automation?.autoCreateSubmissions || false}
                schedule={workflowStatus.config?.schedules?.automaticCreation || '0 7 1 1 *'}
                customAction="Derzeit nicht verfügbar"
              />

              <WorkflowStatusCard
                title="ECR-Codes Import"
                description="Import und Verwaltung hierarchischer ECR-Warencodes"
                icon={faDatabase}
                color="purple"
                isActive={ecrCodes.length > 0}
                onManualTrigger={() => setShowECRImportModal(true)}
                customAction="Excel Import"
                isProcessing={processingAction === 'ecr-demo-import'}
              />

              <WorkflowStatusCard
                title="Marken Import"
                description="Import und Verwaltung von Marken-Daten"
                icon={faClipboardList}
                color="blue"
                isActive={brands.length > 0}
                onManualTrigger={() => setShowBrandImportModal(true)}
                customAction="Excel Import"
                isProcessing={processingAction === 'brand-import'}
              />

              {/* Label Import */}
              <WorkflowStatusCard
                title="Label Import"
                description="Import und Verwaltung von Label-Daten mit Icons"
                icon={faTags}
                color="purple"
                isActive={labels.length > 0}
                onManualTrigger={() => setShowLabelImportModal(true)}
                customAction="Excel Import"
                isProcessing={processingAction === 'label-import'}
              />

              {/* Linien Import */}
              <WorkflowStatusCard
                title="Linien Import"
                description="Import und Verwaltung von Linien-Daten"
                icon={faTags}
                color="green"
                isActive={lines.length > 0}
                onManualTrigger={() => setShowLineImportModal(true)}
                customAction="Excel Import"
                isProcessing={processingAction === 'line-import'}
              />

              {/* Dropdown Options Import */}
              <WorkflowStatusCard
                title="Inhaltseinheiten Import"
                description="Import von Inhaltseinheit-Optionen (kg, g, l, ml, stk)"
                icon={faClipboardList}
                color="blue"
                isActive={false}
                onManualTrigger={() => openDropdownImportModal('unit', 'Inhaltseinheiten')}
                customAction="Excel Import"
                isProcessing={processingAction === 'dropdown-import'}
              />

              <WorkflowStatusCard
                title="Werbeeinheiten Import"
                description="Import von Werbeeinheit-Optionen (STK, KG, L, PAK)"
                icon={faClipboardList}
                color="blue"
                isActive={false}
                onManualTrigger={() => openDropdownImportModal('unit', 'Werbeeinheiten')}
                customAction="Excel Import"
                isProcessing={processingAction === 'dropdown-import'}
              />

              <WorkflowStatusCard
                title="Herkunftsländer Import"
                description="Import von Herkunftsland-Optionen (AT, DE, IT, FR, ES)"
                icon={faClipboardList}
                color="blue"
                isActive={false}
                onManualTrigger={() => openDropdownImportModal('country', 'Herkunftsländer')}
                customAction="Excel Import"
                isProcessing={processingAction === 'dropdown-import'}
              />

              <WorkflowStatusCard
                title="Markant Einheiten Import"
                description="Import von Markant Unit Descriptor Optionen (STK, KA, DI, PAL, LG, UMV)"
                icon={faClipboardList}
                color="purple"
                isActive={true}
                onManualTrigger={() => openDropdownImportModal('markantUnitDescriptor', 'Markant Einheiten')}
                customAction="Excel Import"
                isProcessing={processingAction === 'dropdown-import'}
              />

              <WorkflowStatusCard
                title="Lagerzonen Import"
                description="Import von Lagerzone-Optionen (TK, KU, TR)"
                icon={faClipboardList}
                color="blue"
                isActive={false}
                onManualTrigger={() => openDropdownImportModal('storageZone', 'Lagerzonen')}
                customAction="Excel Import"
                isProcessing={processingAction === 'dropdown-import'}
              />

              <WorkflowStatusCard
                title="Verpackungseinheiten Import"
                description="Import von Verpackungseinheit-Optionen (KA, FL, STK, DI, KI)"
                icon={faClipboardList}
                color="blue"
                isActive={false}
                onManualTrigger={() => openDropdownImportModal('packagingUnit', 'Verpackungseinheiten')}
                customAction="Excel Import"
                isProcessing={processingAction === 'dropdown-import'}
              />

              <WorkflowStatusCard
                title="Grundeinheit Import"
                description="Import von Grundeinheit-Optionen (l, g, ml, kg, m, WG, Stück, Eimer)"
                icon={faClipboardList}
                color="blue"
                isActive={false}
                onManualTrigger={() => openDropdownImportModal('baseUnit', 'Grundeinheiten')}
                customAction="Excel Import"
                isProcessing={processingAction === 'dropdown-import'}
              />

              <WorkflowStatusCard
                title="Basis Vergleichs-Einheitencode Import"
                description="Import von Basis Vergleichs-Einheitencode-Optionen (l, g, ml, kg, m, WG, Stück, Eimer)"
                icon={faClipboardList}
                color="blue"
                isActive={false}
                onManualTrigger={() => openDropdownImportModal('baseComparisonUnitCode', 'Basis Vergleichs-Einheitencodes')}
                customAction="Excel Import"
                isProcessing={processingAction === 'dropdown-import'}
              />

              <WorkflowStatusCard
                title="Fangzone Import"
                description="Import von Fangzone-Optionen"
                icon={faClipboardList}
                color="blue"
                isActive={false}
                onManualTrigger={() => openDropdownImportModal('fangzone', 'Fangzonen')}
                customAction="Excel Import"
                isProcessing={processingAction === 'dropdown-import'}
              />

              <WorkflowStatusCard
                title="Fangmethode Import"
                description="Import von Fangmethode-Optionen"
                icon={faClipboardList}
                color="blue"
                isActive={false}
                onManualTrigger={() => openDropdownImportModal('fangmethode', 'Fangmethoden')}
                customAction="Excel Import"
                isProcessing={processingAction === 'dropdown-import'}
              />

              {/* Perfion Sync */}
              <WorkflowStatusCard
                title="Perfion Sync"
                description="Synchronisation von Artikeldaten mit Perfion API"
                icon={faSync}
                color="blue"
                isActive={true}
                lastRun={perfionSyncTasks.find(t => t.taskType === 'perfion_article_sync')?.lastExecution 
                  ? formatLastRun(perfionSyncTasks.find(t => t.taskType === 'perfion_article_sync')!.lastExecution!)
                  : "Noch nie ausgeführt"}
                schedule={perfionSyncTasks.find(t => t.taskType === 'perfion_article_sync')?.cronSchedule}
                executionStatus={perfionSyncTasks.find(t => t.taskType === 'perfion_article_sync')?.lastExecutionStatus}
                executionDetails={perfionSyncTasks.find(t => t.taskType === 'perfion_article_sync')?.lastExecutionDetails}
                onManualTrigger={handlePerfionSync}
                customAction="Perfion Sync"
                isProcessing={syncingPerfion}
              />

              {/* Lieferanten Perfion Sync */}
              <WorkflowStatusCard
                title="Lieferanten Perfion Sync"
                description="Synchronisation von Lieferantendaten mit Perfion API"
                icon={faUsers}
                color="purple"
                isActive={true}
                lastRun={perfionSyncTasks.find(t => t.taskType === 'perfion_supplier_sync')?.lastExecution 
                  ? formatLastRun(perfionSyncTasks.find(t => t.taskType === 'perfion_supplier_sync')!.lastExecution!)
                  : "Noch nie ausgeführt"}
                schedule={perfionSyncTasks.find(t => t.taskType === 'perfion_supplier_sync')?.cronSchedule}
                executionStatus={perfionSyncTasks.find(t => t.taskType === 'perfion_supplier_sync')?.lastExecutionStatus}
                executionDetails={perfionSyncTasks.find(t => t.taskType === 'perfion_supplier_sync')?.lastExecutionDetails}
                onManualTrigger={handleSupplierPerfionSync}
                customAction="Lieferanten Sync"
                isProcessing={syncingSuppliers}
              />
            </motion.div>
          </div>
        )}

        {/* **MODALS** */}
        {showConfigModal && workflowStatus && (
          <WorkflowConfigModal
            config={workflowStatus.config}
            onClose={() => setShowConfigModal(false)}
            onSave={handleConfigUpdate}
            isProcessing={processingAction === 'config-update'}
          />
        )}

        {showLogsModal && (
          <WorkflowLogsViewer
            logs={logs}
            onClose={() => setShowLogsModal(false)}
            onRefresh={() => loadWorkflowData(true)}
          />
        )}

        {showECRImportModal && (
          <ECRImportModal
            isOpen={showECRImportModal}
            onClose={() => setShowECRImportModal(false)}
            onImportComplete={handleECRImport}
          />
        )}

        {showBrandImportModal && (
          <BrandImportModal
            isOpen={showBrandImportModal}
            onClose={() => setShowBrandImportModal(false)}
            onImportComplete={handleBrandImportComplete}
          />
        )}

        {showLabelImportModal && (
          <LabelImportModal
            isOpen={showLabelImportModal}
            onClose={() => setShowLabelImportModal(false)}
            onImportComplete={handleLabelImportComplete}
          />
        )}

        {showLineImportModal && (
          <LineImportModal
            isOpen={showLineImportModal}
            onClose={() => setShowLineImportModal(false)}
            onImportComplete={handleLineImportComplete}
          />
        )}

        {showDropdownImportModal.isOpen && (
          <DropdownImportModal
            isOpen={showDropdownImportModal.isOpen}
            onClose={() => setShowDropdownImportModal({ isOpen: false, category: '', categoryLabel: '' })}
            category={showDropdownImportModal.category}
            categoryLabel={showDropdownImportModal.categoryLabel}
            onImportComplete={handleDropdownImportComplete}
          />
        )}
      </div>
    </div>
  );
};

export default WorkflowManagement;